import { Component, OnInit } from '@angular/core';
import { BonosService } from 'src/app/shared/services/bonos.service';

/** Un bono de esta cuenta, como lo devuelve `bonos/mis-bonos`. */
interface MiBono {
  id: number;
  codigo: string;
  estado: string;
  moneda: string;
  monto: string;
  condicion: string;
  expiraAt: string | null;
  acreditadoAt: string | null;
  creadoAt: string;
}

/**
 * La pantalla del cliente: escribir el código del bono y ver los que tiene.
 *
 * Cuatro estados, y los cuatro importan:
 *
 *   1. **escribir** — el campo con el código que salió en redes.
 *   2. **retenido** — se reservó el bono pero falta algo para cobrarlo, y se
 *      dice qué falta y hasta cuándo.
 *   3. **acreditado** — la plata ya está en la cuenta.
 *   4. **rechazado** — con el motivo, en un texto que dice qué hacer.
 *
 * El estado 4 es el que se hace mal en general: un «no se pudo» sin explicación
 * manda al cliente a soporte, y soporte no puede contestarle porque tampoco
 * sabe qué chequeo frenó. Los textos vienen redactados del backend y **no se
 * reescriben acá**: son los mismos que ve el operador en su tablero, así que
 * cuando el cliente escribe «me dice que ya reclamé», el operador encuentra
 * exactamente esa fila.
 *
 * Ninguno de esos textos explica cómo saltarse el filtro: «este bono es para
 * otro tipo de cuenta» no dice cuál, porque decirlo es contarle a quien está
 * probando qué le falta para pasar.
 */
@Component({
  selector: 'app-bonos',
  templateUrl: './bonos.component.html',
  styleUrls: ['./bonos.component.sass'],
})
export class BonosComponent implements OnInit {
  cargando = true;

  codigo = '';
  reclamando = false;

  /** Lo que se sabe del código mientras se escribe. `null` = todavía nada. */
  consulta: {
    vale: boolean;
    unidadesRestantes: number | null;
    moneda: string | null;
    monto: string | null;
  } | null = null;
  consultando = false;

  /**
   * Descarta respuestas que llegan tarde.
   *
   * Sin esto, una consulta lenta de «AGUA» puede pisar el resultado de
   * «AGUACATE10» y mostrar un monto que no corresponde al código que el
   * cliente tiene escrito en el campo.
   */
  private sello = 0;

  /** El resultado del último reclamo. `null` mientras no se intentó nada. */
  resultado:
    | { ok: true; estado: string; moneda: string; monto: string; expiraAt: string | null }
    | { ok: false; motivo: string; mensaje: string }
    | null = null;

  bonos: MiBono[] = [];

  constructor(private bonos_: BonosService) {}

  async ngOnInit(): Promise<void> {
    await this.cargarMisBonos();
  }

  private async cargarMisBonos(): Promise<void> {
    this.cargando = true;
    try {
      this.bonos = (await this.bonos_.misBonos()) as MiBono[];
    } catch {
      // Si falla, se muestra el campo igual: no poder listar los bonos viejos
      // no es razón para impedir reclamar uno nuevo.
      this.bonos = [];
    } finally {
      this.cargando = false;
    }
  }

  /** El código como lo va a recibir el backend. Se muestra normalizado. */
  get codigoNormalizado(): string {
    return this.bonos_.normalizarCodigo(this.codigo);
  }

  /**
   * Consulta el código mientras se escribe, con freno entre teclas.
   *
   * Los 400 ms no son cosmética: sin ellos se le pega al backend una vez por
   * caracter, y ese endpoint tiene freno por IP — el propio cliente se
   * bloquearía tipeando su código. El backend igual vuelve a validar todo al
   * reclamar, así que dejar pasar de más acá no habilita nada.
   *
   * Esto **no** decide si puede cobrar: sólo dice si el código existe y cuánto
   * queda. La elegibilidad la deciden los trece chequeos al reclamar.
   */
  async alEscribir(): Promise<void> {
    this.resultado = null;
    this.consulta = null;

    const codigo = this.codigoNormalizado;
    if (codigo.length < 4) return;

    const mio = ++this.sello;
    this.consultando = true;
    await new Promise((r) => setTimeout(r, 400));
    if (mio !== this.sello) return;

    try {
      const r = await this.bonos_.disponibilidad(codigo);
      if (mio !== this.sello) return;
      this.consulta = r;
    } finally {
      if (mio === this.sello) this.consultando = false;
    }
  }

  /** El código existe y la campaña está viva. */
  get codigoVale(): boolean {
    return this.consulta?.vale === true;
  }

  /** Se consultó y no vale. Distinto de «todavía no se consultó». */
  get codigoNoVale(): boolean {
    return this.consulta !== null && this.consulta.vale === false;
  }

  /** Quedan pocas unidades: se avisa para que no lo deje para mañana. */
  get quedanPocas(): boolean {
    const u = this.consulta?.unidadesRestantes;
    return typeof u === 'number' && u > 0 && u <= 10;
  }

  get puedeReclamar(): boolean {
    return this.codigoNormalizado.length >= 4 && !this.reclamando;
  }

  /**
   * Reclama el bono.
   *
   * Un rechazo **no es un error**: el backend responde 200 con el motivo, así
   * que se muestra en la misma pantalla en vez de en un cartel de error. Para
   * el cliente, «este bono se agotó» no es una falla de la aplicación, es la
   * respuesta a lo que preguntó.
   */
  async reclamar(): Promise<void> {
    if (!this.puedeReclamar) return;

    this.reclamando = true;
    this.resultado = null;
    try {
      const r = await this.bonos_.reclamar(this.codigoNormalizado);

      if (r.ok && r.bono) {
        this.resultado = {
          ok: true,
          estado: r.bono.estado,
          moneda: r.bono.moneda,
          monto: r.bono.monto,
          expiraAt: r.bono.expiraAt,
        };
        this.codigo = '';
        await this.cargarMisBonos();
      } else {
        this.resultado = {
          ok: false,
          motivo: r.motivo ?? 'DESCONOCIDO',
          mensaje: r.mensaje ?? 'No se pudo reclamar el bono.',
        };
      }
    } catch (e: any) {
      // Acá sólo llegan los errores de verdad: red caída, sesión vencida, 500.
      this.resultado = {
        ok: false,
        motivo: 'ERROR_RED',
        mensaje:
          e?.error?.message ||
          'No pudimos procesar el reclamo. Revisa tu conexión e intenta de nuevo.',
      };
    } finally {
      this.reclamando = false;
    }
  }

  volverAEscribir(): void {
    this.resultado = null;
  }

  // ------------------------------------------------------------- los estados

  /**
   * El resultado, ya separado en dos por su tipo.
   *
   * Existen para que la plantilla pueda usar `*ngIf="exito as e"`: con la unión
   * cruda, Angular no puede estrechar el tipo dentro del bloque y `e.monto` no
   * compila con `strictTemplates`. Es la forma limpia, no un rodeo.
   */
  get exito(): {
    estado: string;
    moneda: string;
    monto: string;
    expiraAt: string | null;
  } | null {
    return this.resultado?.ok === true ? this.resultado : null;
  }

  get fallo(): { motivo: string; mensaje: string } | null {
    return this.resultado?.ok === false ? this.resultado : null;
  }

  get acreditado(): boolean {
    return this.resultado?.ok === true && this.resultado.estado === 'ACREDITADO';
  }

  get retenido(): boolean {
    return this.resultado?.ok === true && this.resultado.estado === 'RETENIDO';
  }

  get rechazado(): boolean {
    return this.resultado?.ok === false;
  }

  /**
   * El único motivo que se convierte en empujón: terminar la verificación.
   *
   * Es el único donde el cliente tiene algo concreto que hacer y donde
   * hacerlo nos conviene. Los demás no llevan a ninguna acción, y un botón
   * que no lleva a nada es peor que ningún botón.
   */
  get mandaAVerificar(): boolean {
    return this.resultado?.ok === false && this.resultado.motivo === 'CUENTA_NO_APROBADA';
  }

  /** El sobregiro también tiene una acción concreta: cubrir el saldo. */
  get mandaACubrirSaldo(): boolean {
    return this.resultado?.ok === false && this.resultado.motivo === 'CUENTA_EN_SOBREGIRO';
  }

  // --------------------------------------------------------- la lista

  /**
   * El monto como lo lee una persona.
   *
   * El backend manda `5.00000000`: ocho decimales que existen para que las
   * cuentas cierren, no para mostrarse. En la pantalla del cliente eso hace
   * que cinco dólares parezcan un número de serie.
   */
  legible(monto: string | null | undefined): string {
    return this.bonos_.montoLegible(monto);
  }

  /** Qué le falta a un bono retenido, dicho en criollo. */
  faltaTexto(b: MiBono): string {
    if (b.condicion === 'AL_FONDEAR') return 'Se acredita cuando cargues saldo.';
    if (b.condicion === 'AL_PRIMER_ENVIO') return 'Se acredita con tu primer envío.';
    return 'Se está procesando.';
  }

  estadoTexto(estado: string): string {
    if (estado === 'ACREDITADO') return 'Acreditado';
    if (estado === 'RETENIDO') return 'En curso';
    if (estado === 'VENCIDO') return 'Vencido';
    if (estado === 'ANULADO') return 'Anulado';
    return estado.toLowerCase();
  }

  /** Cuántos días le quedan a un bono retenido. `null` si no vence. */
  diasParaVencer(b: MiBono): number | null {
    if (!b.expiraAt || b.estado !== 'RETENIDO') return null;
    const ms = new Date(b.expiraAt).getTime() - Date.now();
    if (!Number.isFinite(ms)) return null;
    return Math.max(0, Math.ceil(ms / 86400000));
  }
}
